import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import opensearchService from '../services/opensearchService';
import './OpenSearchAdmin.css';

const SPR_COLS = ['recordstatus', 'scopetype', 'sourcerecordid', 'targetrecordid', 'current_step', 'updated_at'];
const SCOPE_TYPES   = ['INDIVIDUALS', 'COMPANY', 'AGENT'];
const RECORD_STATUSES = ['PROCESSED', 'WARNING', 'ERROR', 'SKIPPED', 'VAULTED', 'SUCCESS', 'PENDING', 'IN-FLIGHT', 'REQUESTED'];

const fmt = (n) => n != null ? Number(n).toLocaleString() : '-';

const isSprIndex = (name) => name?.startsWith('spr-records-');

export default function OpenSearchIndexDocs() {
    const { indexName } = useParams();
    const navigate = useNavigate();
    const isSpr = isSprIndex(indexName);

    const [docs, setDocs] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [from, setFrom] = useState(0);
    const [expanded, setExpanded] = useState(null);

    // free-text search
    const [inputQ, setInputQ] = useState('');
    const [q, setQ] = useState('');

    // SPR field filters
    const [scopetype, setScopetype]         = useState('');
    const [recordstatus, setRecordstatus]   = useState('');
    const [inputSource, setInputSource]     = useState('');
    const [sourcerecordid, setSourcerecordid] = useState('');
    const [inputTarget, setInputTarget]     = useState('');
    const [targetrecordid, setTargetrecordid] = useState('');

    const debounceQ      = useRef(null);
    const debounceSource = useRef(null);
    const debounceTarget = useRef(null);
    const size = 50;

    const load = useCallback(async (params) => {
        setLoading(true);
        setError(null);
        try {
            const res = await opensearchService.searchDocs(indexName, params);
            setDocs(res.data.hits || []);
            setTotal(res.data.total || 0);
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Failed to load');
            setDocs([]);
        } finally {
            setLoading(false);
        }
    }, [indexName]);

    useEffect(() => {
        load({ q, scopetype, recordstatus, sourcerecordid, targetrecordid, size, from });
    }, [load, q, scopetype, recordstatus, sourcerecordid, targetrecordid, size, from]);

    const handleQ = (val) => {
        setInputQ(val);
        clearTimeout(debounceQ.current);
        debounceQ.current = setTimeout(() => { setFrom(0); setQ(val); }, 400);
    };

    const handleSource = (val) => {
        setInputSource(val);
        clearTimeout(debounceSource.current);
        debounceSource.current = setTimeout(() => { setFrom(0); setSourcerecordid(val); }, 400);
    };

    const handleTarget = (val) => {
        setInputTarget(val);
        clearTimeout(debounceTarget.current);
        debounceTarget.current = setTimeout(() => { setFrom(0); setTargetrecordid(val); }, 400);
    };

    const handleScopetype = (val) => { setFrom(0); setScopetype(val); };
    const handleRecordstatus = (val) => { setFrom(0); setRecordstatus(val); };

    const clearFilters = () => {
        setInputQ(''); setQ('');
        setScopetype(''); setRecordstatus('');
        setInputSource(''); setSourcerecordid('');
        setInputTarget(''); setTargetrecordid('');
        setFrom(0);
    };

    const hasFilters = q || scopetype || recordstatus || sourcerecordid || targetrecordid;

    const cols = docs.length > 0
        ? SPR_COLS.filter(c => docs.some(d => d.source?.[c] != null))
        : SPR_COLS;

    const pages = Math.ceil(total / size);
    const page  = Math.floor(from / size);

    return (
        <div className="acp-page" style={{ maxWidth: 1400 }}>
            <div className="acp-header">
                <div>
                    <button className="os-back-btn" onClick={() => navigate('/admin/opensearch')}>← OpenSearch</button>
                    <h1 className="acp-title os-index-name">{indexName}</h1>
                    <p className="acp-subtitle">{loading ? 'Loading…' : `${fmt(total)} documents`}</p>
                </div>
                <button className="acp-btn acp-btn-secondary" onClick={() => load({ q, scopetype, recordstatus, sourcerecordid, targetrecordid, size, from })} disabled={loading}>
                    {loading ? 'Refreshing…' : 'Refresh'}
                </button>
            </div>

            {error && <div className="os-msg os-msg-error">{error}</div>}

            <div className="acp-card">
                <div className="os-search-bar">
                    <input
                        className="acp-input os-search-input"
                        placeholder="Search all fields…"
                        value={inputQ}
                        onChange={e => handleQ(e.target.value)}
                    />
                    {loading && <span className="os-loading-dot">Searching…</span>}
                    {hasFilters && !loading && (
                        <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={clearFilters}>
                            Clear filters
                        </button>
                    )}
                </div>

                {isSpr && (
                    <div className="os-filter-row">
                        <div className="os-filter-field">
                            <label className="os-filter-label">Scope type</label>
                            <select className="acp-input os-filter-select" value={scopetype} onChange={e => handleScopetype(e.target.value)}>
                                <option value="">All</option>
                                {SCOPE_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div className="os-filter-field">
                            <label className="os-filter-label">Record status</label>
                            <select className="acp-input os-filter-select" value={recordstatus} onChange={e => handleRecordstatus(e.target.value)}>
                                <option value="">All</option>
                                {RECORD_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div className="os-filter-field">
                            <label className="os-filter-label">Source ID</label>
                            <input
                                className="acp-input os-filter-select"
                                placeholder="Partial match…"
                                value={inputSource}
                                onChange={e => handleSource(e.target.value)}
                            />
                        </div>
                        <div className="os-filter-field">
                            <label className="os-filter-label">Target ID</label>
                            <input
                                className="acp-input os-filter-select"
                                placeholder="Partial match…"
                                value={inputTarget}
                                onChange={e => handleTarget(e.target.value)}
                            />
                        </div>
                    </div>
                )}

                {docs.length === 0 && !loading ? (
                    <div className="acp-empty">{hasFilters ? 'No results for these filters' : 'No documents in this index'}</div>
                ) : (
                    <>
                        <div className="os-doc-table-wrap">
                            <table className="acp-table os-doc-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        {cols.map(c => <th key={c}>{c}</th>)}
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {docs.map((d) => (
                                        <React.Fragment key={d.id}>
                                            <tr
                                                className={expanded === d.id ? 'os-doc-row-expanded' : ''}
                                                onClick={() => setExpanded(expanded === d.id ? null : d.id)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <td className="os-doc-id" title={d.id}>{d.id?.substring(0, 8)}…</td>
                                                {cols.map(c => (
                                                    <td key={c} className={c === 'recordstatus' ? `os-status-pill os-status-${(d.source?.[c] || '').toLowerCase().replace(/-/g, '')}` : ''}>
                                                        {d.source?.[c] != null ? String(d.source[c]) : ''}
                                                    </td>
                                                ))}
                                                <td><span className="os-expand-icon">{expanded === d.id ? '▲' : '▼'}</span></td>
                                            </tr>
                                            {expanded === d.id && (
                                                <tr className="os-doc-json-row">
                                                    <td colSpan={cols.length + 2}>
                                                        <pre className="os-doc-json">{JSON.stringify(d.source, null, 2)}</pre>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {pages > 1 && (
                            <div className="os-pagination">
                                <button className="acp-btn acp-btn-secondary acp-btn-sm" disabled={page === 0} onClick={() => setFrom(0)}>«</button>
                                <button className="acp-btn acp-btn-secondary acp-btn-sm" disabled={page === 0} onClick={() => setFrom(Math.max(0, from - size))}>‹</button>
                                <span className="os-page-info">Page {page + 1} of {pages}</span>
                                <button className="acp-btn acp-btn-secondary acp-btn-sm" disabled={page >= pages - 1} onClick={() => setFrom(from + size)}>›</button>
                                <button className="acp-btn acp-btn-secondary acp-btn-sm" disabled={page >= pages - 1} onClick={() => setFrom((pages - 1) * size)}>»</button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
